// Filters and paginates the server-rendered article list client-side:
// Liquid can't filter articles by metafield, so every article is rendered and
// shown/hidden here. The active tag is kept in the URL (?tag=handle) to be shareable.
// ponytail: fine up to ~250 articles (one paginate page); switch to per-tag metaobject pages beyond that.
if (!customElements.get('blog-filter')) {
  customElements.define(
    'blog-filter',
    class BlogFilter extends HTMLElement {
      #items = [];
      #buttons = [];
      #pagination = null;
      #perPage = 6;
      #tag = 'all';
      #page = 1;

      connectedCallback() {
        this.#items = [...this.querySelectorAll('[data-article-tags]')];
        this.#buttons = [...this.querySelectorAll('[data-filter-tag]')];
        this.#pagination = this.querySelector('[data-pagination]');
        this.#perPage = Number(this.dataset.perPage) || this.#perPage;

        this.#buttons.forEach((button) => button.addEventListener('click', () => this.#select(button.dataset.filterTag)));
        this.#pagination?.addEventListener('click', (event) => this.#onPageClick(event));

        const tag = new URL(window.location.href).searchParams.get('tag');
        this.#tag = this.#buttons.some((button) => button.dataset.filterTag === tag) ? tag : 'all';
        this.#render();
      }

      #select(tag) {
        this.#tag = tag;
        this.#page = 1;

        const url = new URL(window.location.href);
        if (tag === 'all') url.searchParams.delete('tag');
        else url.searchParams.set('tag', tag);
        history.replaceState(null, '', url);

        this.#render();
      }

      #onPageClick(event) {
        const button = event.target.closest('[data-page]');
        if (!button) return;

        this.#page = Number(button.dataset.page);
        this.#render();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      #matches(item) {
        // The featured (latest) article already sits in the hero: only list it again when filtering.
        if (this.#tag === 'all') return !item.hasAttribute('data-featured');
        return item.dataset.articleTags.split(' ').includes(this.#tag);
      }

      #render() {
        this.#buttons.forEach((button) =>
          button.setAttribute('aria-pressed', String(button.dataset.filterTag === this.#tag))
        );

        const matching = this.#items.filter((item) => this.#matches(item));
        const pageCount = Math.max(1, Math.ceil(matching.length / this.#perPage));
        this.#page = Math.min(this.#page, pageCount);

        const start = (this.#page - 1) * this.#perPage;
        const visible = new Set(matching.slice(start, start + this.#perPage));
        this.#items.forEach((item) => (item.hidden = !visible.has(item)));

        this.querySelector('[data-empty]')?.toggleAttribute('hidden', matching.length > 0);
        this.#renderPagination(pageCount);
      }

      #renderPagination(pageCount) {
        if (!this.#pagination) return;

        this.#pagination.hidden = pageCount < 2;
        if (pageCount < 2) return;

        const list = this.#pagination.querySelector('[data-pagination-list]');
        const template = this.#pagination.querySelector('template');
        list.replaceChildren(
          ...Array.from({ length: pageCount }, (_, index) => {
            const page = index + 1;
            const item = template.content.firstElementChild.cloneNode(true);
            const button = item.querySelector('button');
            button.dataset.page = page;
            button.textContent = page;
            if (page === this.#page) button.setAttribute('aria-current', 'page');
            return item;
          })
        );

        const next = this.#pagination.querySelector('[data-pagination-next]');
        next.dataset.page = this.#page + 1;
        next.hidden = this.#page === pageCount;
      }
    }
  );
}
